import styled from '@emotion/styled';
import { toArray, uuid4 } from '@racemap/utilities/functions/utils';
import {
  isLineString,
  isPoint,
  isPointOnLineString,
  isPolygon,
} from '@racemap/utilities/functions/validation';
import type {
  EditableGeoObject,
  LineStringObject,
  PointOnLineStringObject,
} from '@racemap/utilities/types/geos';
import { Button, notification, Space } from 'antd';
import type { NotificationInstance } from 'antd/es/notification/interface';
import type { Feature as BasicFeature } from 'geojson';
import { type Immutable, produce } from 'immer';
import type { MapMouseEvent } from 'mapbox-gl';
import { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { toast } from 'src/lib/toast';
import { useControlled } from '../../../../lib/customHooks';
import { boundsForFeatures, fitMapToFeatures } from '../../../../lib/map-helpers';
import { BasicMap, type Props as BasicMapProps } from '../BasicMap/BasicMap';
import { DrawModeControl } from '../Controls/DrawModeControl';
import type { SelectedFeatures } from '../Controls/utils';
import { LabelLayer } from '../Layers/LabelLayer';
import { PointsOnLineStringsLayer } from '../Layers/PointsOnLineStringsLayer';
import { SymbolLayer } from '../Layers/SymbolLayer';
import { MapPing, type MapPingHandle } from '../MapPing';
import DrawControl, { type DrawMode } from './DrawControl';

export interface Feature extends BasicFeature {
  id: string;
}

export type Selection = {
  featureIds: Array<string>;
};

interface ActionParams {
  features: Array<Feature>;
}
interface Props extends BasicMapProps {
  features?: Immutable<Array<EditableGeoObject>>;
  onChange?: (action: 'create' | 'update' | 'delete', features: Array<Feature>) => void;
  onCancel?: () => void;
  selection?: Immutable<Selection>;
  onSelectionChange?: (selection: Immutable<Selection>) => void;
  mode?: DrawMode;
  onModeChange?: (mode: DrawMode) => void;
  showModeControl?: boolean;
  flyToSelection?: boolean;
  zoomRequestNonce?: number;
  fitAllRequestNonce?: number;
  // One-shot signal to play the locator wave at a location (see MapContext.pingRequest).
  pingRequest?: { longitude: number; latitude: number; color?: string; nonce: number } | null;
}

// Modes in which selection changes are allowed to auto-zoom the map. Editing
// modes are excluded so the map doesn't jump around while the user is editing.
const AUTO_ZOOM_MODES: ReadonlyArray<DrawMode> = ['simple_select'];

export const Editor: FC<Props> = ({
  features: controlledFeatures,
  onChange,
  onCancel,
  selection: controlledSelection,
  onSelectionChange,
  children,
  mode: controlledMode,
  onModeChange,
  style,
  showModeControl = true,
  flyToSelection = true,
  zoomRequestNonce,
  fitAllRequestNonce,
  pingRequest,
  ...props
}) => {
  const pingRef = useRef<MapPingHandle>(null);
  const selectionFromMapClickRef = useRef(false);
  const mapCtx = useMap();
  const [api, contextHolder] = notification.useNotification();
  const [mode, setMode] = useControlled<DrawMode>({
    initialValue: 'simple_select',
    controlledValue: controlledMode,
    onChange: onModeChange,
    name: 'Editor.DrawMode',
  });
  const [selection, setSelectedFeatureIds] = useControlled<Immutable<Selection>>({
    initialValue: { featureIds: [] },
    controlledValue: controlledSelection,
    onChange: onSelectionChange,
    name: 'Editor.Selection',
  });
  const [features] = useControlled<Immutable<Array<EditableGeoObject>>>({
    initialValue: [],
    controlledValue: controlledFeatures,
    name: 'Editor.Features',
    onChange: () => {},
  });
  const selectedFeatureIds = useMemo(() => [...(selection?.featureIds || [])], [selection]);

  // TODO: handle all updates in one function and update also the internal state if
  // necessary
  const onCreate = useCallback(
    ({ features }: ActionParams) => {
      onChange?.('create', features);
    },
    [onChange],
  );

  const onUpdate = useCallback(
    ({ features }: ActionParams) => {
      onChange?.('update', features);
    },
    [onChange],
  );

  const onDelete = useCallback(
    async ({ features }: ActionParams) => {
      try {
        await onComponentRemove(api, setMode);
        onChange?.('delete', features);
      } catch {}
    },
    [onChange],
  );

  const handleSelectionChange = useCallback(
    ({ featureIds }: { featureIds: Array<string> }) => {
      setSelectedFeatureIds(
        produce(selection, (draft) => {
          draft.featureIds = featureIds;
        }),
      );
    },
    [setSelectedFeatureIds, selection],
  );
  const handleModeChange = useCallback(
    ({
      mode: newMode,
      featureId,
      featureIds,
    }: {
      mode: DrawMode;
      featureIds?: Array<string>;
      featureId?: string;
    }) => {
      // change mode has to be done before selection ids. some modes need special
      // selected features
      setMode(newMode);
      if (featureId) handleSelectionChange({ featureIds: [featureId] });
      if (featureIds) handleSelectionChange({ featureIds });
    },
    [handleSelectionChange],
  );
  const handlePointOnLineStringClick = useCallback(
    (point: PointOnLineStringObject, _lineString: LineStringObject, evt: MapMouseEvent) => {
      if (mode !== 'simple_select') return;

      selectionFromMapClickRef.current = true;
      handleSelectionChange({ featureIds: [point.id] });
      evt.preventDefault();
    },
    [handleSelectionChange, mode],
  );
  const handleMapSelectionChange = useCallback(
    ({ featureIds }: { featureIds: Array<string> }) => {
      selectionFromMapClickRef.current = true;
      handleSelectionChange({ featureIds });
    },
    [handleSelectionChange],
  );
  const zoomToSelection = useCallback(
    ({ force = false }: { force?: boolean } = {}) => {
      if (props.id === undefined) {
        console.warn("Map doesn't have an id. Can't use flyToSelection");
        return;
      }
      const map = mapCtx[props.id];
      if (map == null) return;

      // Don't auto-zoom while actively editing — only allow explicit zoom (force=true)
      if (!force && !AUTO_ZOOM_MODES.includes(mode)) return;

      const fromMapClick = selectionFromMapClickRef.current;
      selectionFromMapClickRef.current = false;

      if (flyToSelection && selectedFeatureIds.length > 0) {
        const selectedFeatures = features.filter((f) => selectedFeatureIds.includes(f.id));

        // Always play a one-time locator wave for a single selected point feature,
        // whether the map stays put (already visible) or pans/zooms to it.
        const feature = selectedFeatures[0];
        if (selectedFeatures.length === 1 && (isPoint(feature) || isPointOnLineString(feature))) {
          const [lng, lat] = feature.geometry.coordinates as [number, number];
          pingRef.current?.ping({
            longitude: lng,
            latitude: lat,
            color: feature.properties?.color ?? undefined,
          });
        }

        // Skip zoom when the user clicked directly on the map — they already see the feature.
        // Only zoom when selection comes from an external source (e.g. the Tracks & Lines menu).
        if (!force && fromMapClick) return;

        const featureBounds = boundsForFeatures(selectedFeatures);
        const mapBounds = map.getBounds();
        const featuresInView =
          !featureBounds.isEmpty() &&
          mapBounds != null &&
          mapBounds.contains(featureBounds.getSouthWest()) &&
          mapBounds.contains(featureBounds.getNorthEast());

        if (!featuresInView || force) {
          fitMapToFeatures(map.getMap(), selectedFeatures);
        }
      }
    },
    [flyToSelection, selectedFeatureIds, features, mapCtx, props.id, mode],
  );

  // Explicit "zoom to all items" for the map tools control — fits the viewport
  // to the bounds of every editable feature (tracks, POIs, splits).
  const zoomToAllFeatures = useCallback(() => {
    if (props.id === undefined) return;
    const map = mapCtx[props.id];
    if (map == null) return;

    const bounds = boundsForFeatures(features);
    if (bounds.isEmpty()) return;

    map.getMap().fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 800 });
  }, [features, mapCtx, props.id]);

  useEffect(() => {
    zoomToSelection();
  }, [zoomToSelection]);

  // Explicit "zoom now" signal from outside (e.g. the Tracks/POI menu's Modify
  // action), which force-zooms regardless of the current edit mode.
  const prevZoomRequestNonce = useRef(zoomRequestNonce);
  useEffect(() => {
    if (zoomRequestNonce === prevZoomRequestNonce.current) return;
    prevZoomRequestNonce.current = zoomRequestNonce;
    zoomToSelection({ force: true });
  }, [zoomRequestNonce, zoomToSelection]);

  // Explicit "fit to all features" signal from outside (e.g. after a drag-and-drop
  // import), mirroring the global zoom button.
  const prevFitAllRequestNonce = useRef(fitAllRequestNonce);
  useEffect(() => {
    if (fitAllRequestNonce === prevFitAllRequestNonce.current) return;
    prevFitAllRequestNonce.current = fitAllRequestNonce;
    zoomToAllFeatures();
  }, [fitAllRequestNonce, zoomToAllFeatures]);

  // Play the locator wave when an external source requests a ping (e.g. hovering a
  // split in the elevation chart). Nonce-gated so each request fires exactly once.
  const prevPingNonce = useRef(pingRequest?.nonce);
  useEffect(() => {
    if (pingRequest == null || pingRequest.nonce === prevPingNonce.current) return;
    prevPingNonce.current = pingRequest.nonce;
    pingRef.current?.ping({
      longitude: pingRequest.longitude,
      latitude: pingRequest.latitude,
      color: pingRequest.color,
    });
  }, [pingRequest]);

  const pointsOnLineStrings = features.filter(isPointOnLineString);
  const lineStrings = features.filter(isLineString);
  const points = features.filter(isPoint);
  const polygons = features.filter(isPolygon);
  const pointsWithIcon = points.filter((f) => f.properties?.icon);
  const selectedFeatures = features.filter((f) => selectedFeatureIds.includes(f.id));

  children = [
    <PointsOnLineStringsLayer
      key="points-on-linestrings-layer"
      points={pointsOnLineStrings}
      lineStrings={lineStrings}
      selectedFeatureIds={selectedFeatureIds}
      onClick={handlePointOnLineStringClick}
    />,
    <SymbolLayer points={pointsWithIcon} key="symbol-layer" />,
    <LabelLayer
      labelFeatures={[...points, ...polygons]}
      key="label-layer"
      beforeId={'near-by-mouse-point.hot'}
    />,
    <DrawControl
      key="draw-control"
      features={features}
      position="bottom-left"
      displayControlsDefault={false}
      mode={mode}
      selectedFeatureIds={selectedFeatureIds}
      onModeChange={handleModeChange}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
      onSelectionChange={handleMapSelectionChange}
      onError={(e) => toast.error(e.error)}
    />,
    ...toArray(children),
  ];

  children.push(<MapPing key="ping" ref={pingRef} />);

  if (showModeControl) {
    children.push(
      <DrawModeControl
        key="draw-mode-control"
        mode={mode}
        selectedFeatures={selectedFeatures}
        allFeatures={features as unknown as SelectedFeatures}
        onChange={(mode) => handleModeChange({ mode })}
        onZoomToSelection={() => zoomToSelection({ force: true })}
        onCancel={onCancel}
      />,
    );
  }

  return (
    <Container style={style}>
      {contextHolder}
      <BasicMap {...props} onFitToAll={zoomToAllFeatures}>
        {children}
      </BasicMap>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;

  // makes the autogenerated controls of the DrawControl invisible
  .mapbox-gl-draw_line {
    display: none;
  }
  .mapbox-gl-draw_polygon {
    display: none;
  }
  .mapbox-gl-draw_point {
    display: none;
  }
  .mapbox-gl-draw_trash {
    display: none;
  }
  .mapbox-gl-draw_combine {
    display: none;
  }
  .mapbox-gl-draw_uncombine {
    display: none;
  }
`;

const onComponentRemove = (
  notificationInstance: NotificationInstance,
  setMode: (mode: DrawMode) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const key = `remove-component-${uuid4()}`;
    const btn = (
      <Space>
        <Button
          type="primary"
          danger
          size="small"
          onClick={() => {
            setMode('simple_select');
            notificationInstance.destroy(key);
            resolve();
          }}
        >
          Confirm
        </Button>
      </Space>
    );
    notificationInstance.open({
      message: 'Remove Element(s)',
      description: 'Are you sure you want to remove this element(s)?',
      btn,
      key,
      onClose: () => reject(),
    });
  });
};
